import { Check, ChevronDown } from 'lucide-react'
import { useMemo } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui'
import { cn } from '@/utils'

import { COMMENT_STATUS_STYLES } from '../../constants'
import { ECommentStatus } from '../../types'

const itemBase =
  'flex items-center justify-between px-2 py-1.5 text-sm rounded-sm text-primary-900 transition-colors'
const itemActive = 'bg-gray-100 cursor-default'
const itemInactive = 'hover:bg-gray-100 cursor-pointer'

interface CommentStatusProps {
  readonly threadStatus: string | undefined
  readonly isViewer: boolean
  readonly isLatestVersion: boolean
  readonly isOrgAdminOrProjectLeadOrAuthor: boolean
  readonly onReopenThread: () => void
  readonly onMarkAsResolved: () => void
}

export function CommentStatus({
  threadStatus,
  isViewer,
  isLatestVersion,

  isOrgAdminOrProjectLeadOrAuthor,
  onReopenThread,
  onMarkAsResolved,
}: Readonly<CommentStatusProps>) {
  const isStatusOpen = useMemo(
    () => threadStatus === ECommentStatus.OPEN,
    [threadStatus]
  )

  const isStatusResolved = useMemo(
    () => threadStatus === ECommentStatus.RESOLVED,
    [threadStatus]
  )

  const currentStatusStyles = useMemo(
    () =>
      COMMENT_STATUS_STYLES[
        (threadStatus?.toLowerCase() as 'open' | 'resolved') || 'open'
      ],
    [threadStatus]
  )

  if (isViewer) return null

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={!isLatestVersion || !isOrgAdminOrProjectLeadOrAuthor}
      >
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 rounded-full border border-solid px-1.5 py-0.5 text-xs font-medium hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors',
            currentStatusStyles.bg,
            currentStatusStyles.text,
            currentStatusStyles.border
          )}
        >
          <span className="leading-none capitalize">
            {threadStatus?.toLowerCase()}
          </span>
          <ChevronDown className={cn('h-4 w-4', currentStatusStyles.text)} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="sm:w-[151px] sm:min-w-none" align="start">
        <DropdownMenuItem
          className={cn(itemBase, isStatusOpen ? itemActive : itemInactive)}
          disabled={isStatusOpen}
          onSelect={(e) => {
            if (isStatusOpen) {
              e.preventDefault()
              return
            }
            onReopenThread()
          }}
        >
          <span>Open</span>
          {isStatusOpen && <Check className="h-4 w-4 text-blue-600" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          className={cn(itemBase, isStatusResolved ? itemActive : itemInactive)}
          disabled={isStatusResolved}
          onSelect={(e) => {
            if (isStatusResolved) {
              e.preventDefault()
              return
            }
            onMarkAsResolved()
          }}
        >
          <span>Resolved</span>
          {isStatusResolved && <Check className="h-4 w-4 text-blue-600" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
